import { PlayerError } from '@foundation-player/error-codes';
import { FetchService } from '@foundation-player/fetch-service';
import { getLogger } from '@foundation-player/logger';
import { getEngineVersion, isAndroid, isChromiumEngine } from '@foundation-player/platform-check';
import { ManifestPriority, ManifestType, SourceType } from '@foundation-player/runtime';
import type {
  AdSlot,
  ConsentCallback,
  FoundationError,
  Manifest,
  ManifestConfig,
  MediaConfig,
  PlayerBitrate,
  ProgressiveConfig,
  SourceTextTrack,
  StUSConfig,
  TextTrack,
  UpdatableMediaMetadata,
  VideoThumbnail,
} from '@foundation-player/types';
import { v4 as uuidv4 } from 'uuid';
import { PlayerAdapter } from './adapter';
import { AdSlotType, AdvertisingAdapter } from './advertising';
import { AutoplayDetection } from './autoplay/autoplay-detection';
import { ClientSupport } from './capability/client-capabilities';
import { PlayerEvent } from './event-emitter/events';
import { PlayerMetric } from './event-emitter/player-metric';
import {
  FoundationDestroy,
  FoundationErrorEvent,
  FoundationEvent,
  FoundationEventBase,
  MetadataUpdatedEvent,
  MetricEvent,
} from './event-emitter/unfiltered';
import { ManifestResolver } from './manifest/manifest-resolver';
import { SourceResolver } from './manifest/source-resolver';
import { Media } from './media';
import { MediaManager } from './media-manager/media-manager';
import { Thumbnails } from './media-manager/thumbnails';
import { ContentState } from './media-manager/types';
import { AdvertisingSubtitleObserver } from './observers/advertising-subtitle-observer';
import { ErrorObserver } from './observers/error-observer';
import { PlaybackSpeedObserver } from './observers/playback-speed-observer';
import { PlaybackSession } from './playback-session';
import { PlayerConfig } from './player-config';
import { PlayerContainer } from './player-container';
import { PlayerInterface } from './player-interface';
import { PlayerPlugin, PublicPluginApi, hasPublicApi } from './plugin/player-plugin';
import { RequestHeader, RequestHeaderCallback, RequestType } from './request/request-header';
import { StUSResolver } from './stus-resolver';
import { getVersion } from './version';
import { VttTracksContainer } from './vtt/vtt-tracks-container';

export interface FoundationPlayerParams {
  playerId: string;
  playerConfig?: PlayerConfig;
  playerAdapter: PlayerAdapter;
  playerContainer: PlayerContainer;
  playerPlugins?: PlayerPlugin[];
  autoplayDetection: AutoplayDetection;
  clientSupport: ClientSupport;
  remoteConfig: unknown;
  userConfig: unknown;
  mediaManager?: MediaManager;
  fetchService?: FetchService;
}

export class FoundationPlayer implements PlayerInterface {
  private readonly playerAdapter: PlayerAdapter;
  private readonly playerPlugins: PlayerPlugin[];
  private readonly errorObserver: ErrorObserver;
  private readonly mediaManager: MediaManager;
  private readonly playbackSpeedObserver: PlaybackSpeedObserver;
  private readonly advertisingSubtitleObserver: AdvertisingSubtitleObserver;
  private readonly autoplayDetection: AutoplayDetection;
  private readonly manifestResolver: ManifestResolver;
  private readonly sourceResolver: SourceResolver;
  private readonly remoteConfig: FoundationPlayerParams['remoteConfig'];
  private readonly userConfig: FoundationPlayerParams['userConfig'];
  private readonly fetchService: FetchService;
  private readonly log = getLogger('player.core.FoundationPlayer');
  private readonly destroyLocks: Set<string> = new Set<string>();
  private readonly publicPluginApis = new Map<string, PublicPluginApi<string, unknown>>();
  private readonly playerId: string;
  private readonly playerConfig: PlayerConfig;
  private readonly playerContainer: PlayerContainer;
  private readonly clientSupport: ClientSupport;
  private readonly stusResolver: StUSResolver;

  private playbackSession?: PlaybackSession;
  private consentCallback?: ConsentCallback;
  private requestHeaderCallback?: RequestHeaderCallback;
  private advertisingAdapter?: AdvertisingAdapter;
  private destroyed = false;
  private textTrackMode: 'Vtt' | 'PlayerTextTracks' = 'PlayerTextTracks';
  private vttTracksContainer?: VttTracksContainer;

  private beforeUnloadListener?: () => void;

  constructor(params: FoundationPlayerParams) {
    this.playerId = params.playerId;
    this.playerContainer = params.playerContainer;
    const playerElement = this.getPlayerElement();
    playerElement.setAttribute('data-foundation-player', '');
    playerElement.getPlayer = () => this;

    this.clientSupport = params.clientSupport;
    this.playerConfig = params.playerConfig || {};
    this.playerAdapter = params.playerAdapter;
    this.autoplayDetection = params.autoplayDetection;
    this.playerAdapter.setMaxBitrate(this.playerConfig.maxBitrate || {});
    this.playerPlugins = params.playerPlugins || [];
    this.playerPlugins.forEach((plugin) => {
      if (hasPublicApi(plugin)) {
        const key = plugin.getKey();
        if (key) {
          if (this.publicPluginApis.has(key)) {
            throw new FoundationErrorEvent(PlayerEvent.Error, {
              message: `duplicate plugin name for ${key}`,
              code: PlayerError.PLAYER_DEPENDENCY_INVALID_52130.code,
            });
          }
          this.publicPluginApis.set(key, plugin);
        }
      }
    });
    this.remoteConfig = params.remoteConfig;
    this.userConfig = params.userConfig;

    this.mediaManager = params.mediaManager || new MediaManager(this);
    this.errorObserver = new ErrorObserver(this);

    this.fetchService = params.fetchService || new FetchService();

    this.manifestResolver = new ManifestResolver();
    this.sourceResolver = new SourceResolver();
    this.playbackSpeedObserver = new PlaybackSpeedObserver(this);
    this.advertisingSubtitleObserver = new AdvertisingSubtitleObserver(this);
    this.stusResolver = new StUSResolver(this.playerConfig, this.fetchService);

    const hasBeforeWindowUnloadHooks = this.playerPlugins.some((plugin) => plugin.beforeWindowUnload);
    if (hasBeforeWindowUnloadHooks) {
      let unloadSent = false;
      this.beforeUnloadListener = () => {
        if (unloadSent) {
          return;
        }
        unloadSent = true;
        for (const plugin of this.playerPlugins) {
          if (plugin.beforeWindowUnload) {
            try {
              plugin.beforeWindowUnload();
            } catch (e) {
              // The hook must never bubble up an error, thus we catch and only log here.
              // eslint-disable-next-line no-console
              console.error('beforeWindowUnload hook failed for plugin', plugin, e);
            }
          }
        }
      };

      window.addEventListener('beforeunload', this.beforeUnloadListener);
      window.addEventListener('pagehide', this.beforeUnloadListener);
    }

    this.finalizeInitialization();

    this.log.debug('Running Foundation player', {
      uuid: this.playerId,
      version: this.getVersion(),
      rootPlayer: this.playerAdapter.getPlayerType(),
    });
    this.log.debug('Initial PlayerConfig:', this.playerConfig);
  }

  getPlugin<T>(key: string): T {
    return this.publicPluginApis.get(key)?.getPlugin() as T;
  }

  /**
   * Must be called at the end of player initialization
   */
  private finalizeInitialization() {
    const initializationEvent = new Event(PlayerEvent.PlayerCreated, { bubbles: true });
    this.dispatchEvent(initializationEvent);
  }

  getConsent(timeoutInMs?: number): Promise<string | undefined> {
    if (this.consentCallback) {
      if (timeoutInMs) {
        return Promise.race([
          this.consentCallback(timeoutInMs),
          new Promise<undefined>((resolve) => setTimeout(resolve, timeoutInMs)),
        ]);
      } else {
        return this.consentCallback();
      }
    }

    return Promise.resolve(undefined);
  }

  setConsentCallback(callback: ConsentCallback) {
    this.consentCallback = callback;
  }

  getAdvertisingAdapter() {
    return this.advertisingAdapter;
  }

  setAdvertisingAdapter(advertisingAdapter: AdvertisingAdapter) {
    this.advertisingAdapter = advertisingAdapter;
  }

  getRequestHeader<T = RequestType>(type: RequestType & T): Promise<RequestHeader | undefined> {
    return Promise.resolve(this.requestHeaderCallback?.(type));
  }

  setRequestHeaderCallback(callback: RequestHeaderCallback) {
    this.requestHeaderCallback = callback;
    this.playerAdapter.setRequestHeaderCallback(callback);
  }

  async loadMedia(mediaConfig: MediaConfig<ManifestConfig | ProgressiveConfig | StUSConfig>) {
    try {
      if (this.destroyed) {
        throw new FoundationErrorEvent(PlayerEvent.Error, {
          message: `This player was destroyed and must not be used anymore!`,
          code: PlayerError.PLAYER_ALREADY_DESTROYED_35100.code,
        });
      }

      const loadMediaStartTime = performance.now();

      await this.stopAdSlotIfNecessary();

      this.listenOnce(PlayerEvent.MediaLoaded, () => {
        const loadMediaEndTime = performance.now();
        this.dispatchEvent(
          new MetricEvent(PlayerMetric.MediaLoad, { start: loadMediaStartTime, end: loadMediaEndTime }),
        );
      });

      this.log.debug('Initial MediaConfig:', mediaConfig);

      const resolvedMediaConfig = await this.resolveMediaConfig(mediaConfig);
      this.playbackSession = {
        id: uuidv4(),
        mediaConfig: resolvedMediaConfig,
        contentSources: [],
        adSlots: resolvedMediaConfig.advertising?.slots || [],
      };

      await Promise.all(
        this.playerPlugins.map(async (plugin) => {
          if (plugin.setupSession) {
            try {
              await plugin.setupSession();
            } catch (e) {
              this.log.error('Setup session hook failed for plugin', {
                plugin,
                error: e,
              });
            }
          }
        }),
      );

      const playerCapabilities = this.playerAdapter.getCapabilities();
      const clientSupport = this.getClientSupport();

      const manifests = this.manifestResolver.resolve(resolvedMediaConfig, playerCapabilities, clientSupport);
      let thumbnails: Thumbnails | undefined;
      if (resolvedMediaConfig.thumbnails?.url) {
        thumbnails = new Thumbnails(resolvedMediaConfig.thumbnails.url, resolvedMediaConfig.thumbnails.preload);
      }

      // We only pass the first manifest, because since PLAYHTML5-428 a manifest with higher quality will also contain
      // the lower quality sources. This is done to improve the fallback behavior and make it simpler in general.
      const contentSources = await this.createContentSources(manifests[0]);
      this.playbackSession = {
        ...this.playbackSession,
        thumbnails,
        contentSources,
      };

      if (resolvedMediaConfig.textTracks) {
        // If the StUS returns text tracks, we prefer to use them instead
        // of the text tracks within the source file
        await this.loadTextTracks(resolvedMediaConfig.textTracks);
        if (this.getVttTracksContainer().getTextTracks().length > 0) {
          this.useVttTracks();
        } else {
          this.log.debug('No valid text track found in list, not enabling VTT mode', resolvedMediaConfig.textTracks);
        }
      }

      await this.advertisingAdapter?.onLoadMedia(this.playbackSession.adSlots);
      this.mediaManager.startSession(this.playbackSession.id);
      await this.initializePlayout(this.playbackSession.contentSources[0]);
    } catch (e) {
      const isFoundationError = e && typeof e === 'object' && 'type' in e && e.type === PlayerEvent.Error;
      const error = isFoundationError
        ? (e as FoundationErrorEvent)
        : new FoundationErrorEvent(PlayerEvent.Error, {
            code: PlayerError.ERROR_NOT_TREATED_59000.code,
            message: 'Load media has thrown a non foundation error!',
            originalEvent: e,
          });
      this.dispatchEvent(error);

      throw error;
    }
  }

  private async createContentSources(manifest: Manifest): Promise<Media[]> {
    if (!this.playbackSession) {
      return [];
    }

    const resolvedMediaConfig = this.playbackSession.mediaConfig;
    const isAutoplayRequested = resolvedMediaConfig.autoplay ?? this.playerConfig.autoplay ?? false;
    const isMuted = resolvedMediaConfig.muted ?? this.playerConfig.muted ?? false;
    const playbackSpeed = resolvedMediaConfig.playbackSpeed ?? this.playerConfig.playbackSpeed ?? 1.0;
    const isAutoplaySupported = await this.isAutoplaySupported(isAutoplayRequested, isMuted);

    // We only use the source resolver for validation for a simple sanity check.
    // The actual source selection will be by trial and error going through the sources.
    this.sourceResolver.resolve(manifest);
    return manifest.sources.map(
      (source) =>
        new Media({
          // playbackSession is checked at the start of the method
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          sessionId: this.playbackSession!.id,
          id: resolvedMediaConfig.id,
          type: resolvedMediaConfig.type,
          autoplayRequested: isAutoplayRequested,
          autoplaySupported: isAutoplaySupported,
          muted: isMuted,
          startTime: resolvedMediaConfig.startTime ?? 0,
          playbackSpeed,
          manifest,
          source,
          metadata: resolvedMediaConfig.metadata,
        }),
    );
  }

  private isAutoplaySupported(isAutoplayRequested: boolean, isMuted: boolean): Promise<boolean> {
    // If an asset was already started successfully, future content may be played automatically
    if (this.mediaManager.isInitiallyStarted()) {
      return Promise.resolve(true);
    }

    return this.autoplayDetection.isAutoplaySupported(this.getVideoElement(), isAutoplayRequested, isMuted);
  }

  /**
   * Resolves the user provided media config to a uniform format that can be used by the player
   *
   * @param mediaConfig - The user provided media config
   */
  private async resolveMediaConfig(
    mediaConfig: MediaConfig<ManifestConfig | ProgressiveConfig | StUSConfig>,
  ): Promise<MediaConfig> {
    type MaybeManifestConfig = {
      manifests?: Manifest[];
      type?: SourceType;
    };

    if ('manifest' in mediaConfig && mediaConfig.manifest) {
      this.transformManifestConfig(mediaConfig as MediaConfig<ManifestConfig & ProgressiveConfig>);
    }

    const headers = (await this.getRequestHeader(RequestType.DRM)) as HeadersInit;
    const resolvedMediaConfig = (await this.stusResolver.resolveMediaConfig(
      mediaConfig as MediaConfig<StUSConfig>,
      headers,
    )) as MediaConfig;

    if (
      // guard for legacy config and wrong usage
      !(resolvedMediaConfig as MaybeManifestConfig).manifests ||
      // guard for legacy config and wrong usage
      !(resolvedMediaConfig as MaybeManifestConfig).type ||
      !Array.isArray(resolvedMediaConfig.manifests) ||
      resolvedMediaConfig.manifests.length === 0
    ) {
      throw new FoundationErrorEvent(PlayerEvent.Error, {
        message: `Either manifests or type were not provided in the media config`,
        code: PlayerError.NO_MANIFEST_IN_CONFIG_34210.code,
        context: {
          stus: {
            mediaId: mediaConfig.id,
            mediaType: mediaConfig.type,
            config: this.playerConfig.streamingURLService,
          },
          mediaConfig,
        },
      });
    }

    return resolvedMediaConfig;
  }

  async initializePlayout(media: Media) {
    await this.stopAdSlotIfNecessary();

    this.log.debug('Initializing playout with media', media);

    // we need to check if autoplay is supported for the media again, because the state might have changed
    if (media.autoplayRequested && !media.autoplaySupported) {
      const isAutoplaySupported = await this.isAutoplaySupported(true, media.muted);
      if (isAutoplaySupported) {
        media = new Media({ ...media, autoplaySupported: true });
      }
    }

    for (const plugin of this.playerPlugins) {
      if (plugin.prepareMediaSource) {
        const newSource = await plugin.prepareMediaSource(media);
        if (newSource !== media.source) {
          this.log.debug('A plugin patched the media source through "prepareMediaSource" hook', {
            previousSource: media.source,
            newSource,
            plugin,
          });
          media = new Media({ ...media, source: newSource });
        }
      }
    }

    for (const plugin of this.playerPlugins) {
      if (plugin.beforeLoadMedia) {
        const newMedia = await plugin.beforeLoadMedia(media);
        if (newMedia !== media) {
          this.log.debug('A plugin patched the media through "beforeLoadMedia" hook', {
            media,
            newMedia,
            plugin,
          });
          media = newMedia;
        }
      }
    }

    /**
     * Removes the poster on playback, so it does not bleed through between ads and content.
     *
     * Sadly this lead to a bug in Chromium 122+ on Android, where the Bitmovin player does not play the video.
     * Because of that we don't remove the poster on those devices.
     *
     * @remarks
     * Chromium 122+ on Android has a bug that causes the Bitmovin player to not play the video when
     * the poster is removed. This affects only playback with Bitmovin on Android with Chromium based
     * browsers.
     *
     * The Bitmovin player will throw an error "PlayerError(1000/UNKNOWN) The video element has thrown a media error."
     * directly to the console. An error event is only emitted without ads.
     */
    if (!this.hasPosterFrameBug()) {
      this.listenOnce(PlayerEvent.TimeChanged, () => {
        this.getVideoElement()?.removeAttribute('poster');
      });
    }

    return this.mediaManager.initializePlayout(media);
  }

  getThumbnail(second: number): Promise<VideoThumbnail | undefined> {
    return Promise.resolve(this.playbackSession?.thumbnails?.getThumbnail(second));
  }

  /**
   * Transform a single manifest entry to our array format so adapters only have to work with the array format
   *
   * @param mediaConfig
   * @private
   */
  private transformManifestConfig(mediaConfig: MediaConfig<ManifestConfig & ProgressiveConfig>) {
    let manifestType: ManifestType = ManifestType.Progressive;

    // will most likely be SD when no DRM is used
    if (mediaConfig.manifest.endsWith('.mpd')) {
      manifestType = ManifestType.DashSD;
    } else if (mediaConfig.manifest.endsWith('.m3u8')) {
      manifestType = ManifestType.HlsSD;
    }

    // guard for legacy and progressive config
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!mediaConfig.type) {
      // This is only a workaround to provide something for progressive content.
      // In general, we expect the user to supply a full ManifestConfig instead
      // ProgressiveConfig should only be used for simple progressive vod content
      mediaConfig.type = SourceType.VOD;
    }

    mediaConfig.manifests = [
      {
        type: manifestType,
        sources: [
          {
            url: mediaConfig.manifest,
            priority: ManifestPriority.Main,
          },
        ],
      },
    ];
  }

  async play() {
    if (!this.mediaManager.isInitiallyStarted()) {
      const playStartTime = performance.now();
      this.listenOnce(PlayerEvent.TimeChanged, () => {
        const playEndTime = performance.now();
        this.dispatchEvent(new MetricEvent(PlayerMetric.MediaPlay, { start: playStartTime, end: playEndTime }));
      });
    }

    // Only interact with the ad player if the ad actually started
    if (this.getActiveAdSlot(true)) {
      await (this.advertisingAdapter as AdvertisingAdapter).resumeAd();
    } else {
      // This handles preroll ads
      await this.mediaManager.playLoadedMediaManually();
    }
  }

  async pause() {
    const adSlot = this.getActiveAdSlot(true);
    // Only interact with the ad player if the ad actually started
    if (adSlot) {
      // Interactive Ads will be ignored completly
      if (!adSlot.getCurrentClip()?.isInteractive) {
        await (this.advertisingAdapter as AdvertisingAdapter).pauseAd();
      }
    } else {
      if (this.isPlayerReady()) {
        this.playerAdapter.pause();
      } else {
        this.dispatchEvent(new FoundationEventBase(PlayerEvent.Paused));
        this.mediaManager.disableAutoplay();
      }
    }
  }

  isPlaying() {
    const adSlot = this.advertisingAdapter?.getCurrentAdSlot();

    if (adSlot?.isActive() && adSlot.getType() !== AdSlotType.NonLinear) {
      return !(this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().isPaused();
    }

    if (!this.isPlayerReady()) {
      return false;
    }

    return this.playerAdapter.isPlaying();
  }

  async mute() {
    if (this.getActiveAdSlot(true)) {
      await (this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().mute();
    }
    this.playerAdapter.mute();
  }

  async unmute() {
    if (this.getActiveAdSlot(true)) {
      await (this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().unmute();
    }
    this.playerAdapter.unmute();
  }

  isMuted() {
    if (this.getActiveAdSlot(true)) {
      return (this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().isMuted();
    }
    return this.playerAdapter.isMuted();
  }

  getVolume(): number {
    if (this.getActiveAdSlot(true)) {
      return (this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().getVolume();
    }
    return this.playerAdapter.getVolume();
  }

  async setVolume(volume: number) {
    const clampedVolume = Math.min(Math.max(volume, 0), 1);

    if (this.getActiveAdSlot(true)) {
      await (this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().setVolume(clampedVolume);
    }
    this.playerAdapter.setVolume(clampedVolume);
  }

  getCurrentTime(exact = false) {
    if (this.getActiveAdSlot(true)) {
      return (this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().getCurrentTime();
    }
    return this.playerAdapter.getCurrentTime(exact);
  }

  isLive() {
    return this.playerAdapter.isLive();
  }

  getVersion(): string {
    return getVersion();
  }

  isSupportedPlatform(): boolean {
    return this.playerAdapter.isSupportedPlatform();
  }

  seek(offset: number): void {
    // Seek is only allowed during content playback
    if (!this.getActiveAdSlot()) {
      this.playerAdapter.seek(offset);
    }
  }

  isSupportedDRMPlatform(): Promise<boolean> {
    return Promise.resolve(this.playerAdapter.isSupportedDRMPlatform());
  }

  getDuration(type: 'content' | 'current_playing' = 'current_playing') {
    const isAdActive = this.getActiveAdSlot(true);

    if (type === 'current_playing' && isAdActive) {
      return (this.advertisingAdapter as AdvertisingAdapter).getAdPlayer().getDuration() || 0;
    }

    if (type === 'content' && isAdActive) {
      return this.playerAdapter.getCachedDuration();
    }

    return this.playerAdapter.getDuration();
  }

  canAutoplay(): boolean {
    return this.mediaManager.canAutoplay();
  }

  isFullscreen(): boolean {
    return this.playerContainer.isFullscreen();
  }

  getPlaybackSpeed(): number {
    return this.playerAdapter.getPlaybackSpeed();
  }

  /**
   * FIXME: We should disable setting the playbackSpeed during an AdSlot. Right now,
   * the AdSlot lifecycle events and the AdSlot state is not aligned and produces
   * errors. There is a ticket for that: PLAYHTML5-117
   *
   * @See https://rtltech.atlassian.net/browse/PLAYHTML5-117
   */
  setPlaybackSpeed(playbackSpeed: number) {
    this.playerAdapter.setPlaybackSpeed(playbackSpeed);
  }

  isPlayerReady(): boolean {
    return this.mediaManager.isPlayerReady() || false;
  }

  async unload() {
    await this.playerAdapter.unload();
    this.dispatchEvent(new FoundationEventBase(PlayerEvent.SourceUnloaded));
  }

  isPlayerAvailable() {
    return this.playerAdapter.isPlayerAvailable();
  }

  getMediaURL() {
    return this.playerAdapter.getMediaURL();
  }

  getAvailableVideoTracks() {
    return this.playerAdapter.getAvailableVideoTracks();
  }

  getActiveVideoTrack() {
    return this.playerAdapter.getActiveVideoTrack();
  }

  getCurrentBitrate() {
    return this.getActiveVideoTrack()?.bitrate;
  }

  setMaxBitrate(bitrate: Partial<PlayerBitrate>) {
    this.playerAdapter.setMaxBitrate(bitrate);
  }

  getMaxBitrate() {
    return this.playerAdapter.getMaxBitrate();
  }

  getAvailableAudioTracks() {
    if (this.mediaManager.getMedia()?.ovAudio) {
      return this.playerAdapter.getAvailableAudioTracks();
    }

    const germanAudioTracks = this.playerAdapter
      .getAvailableAudioTracks()
      .filter((audioTrack) => audioTrack.language === 'de');
    //if no german audio is available, OV is always allowed (FOUN-2301)
    if (germanAudioTracks.length === 0) {
      return this.playerAdapter.getAvailableAudioTracks();
    }
    return germanAudioTracks;
  }

  getActiveAudioTrack() {
    return this.playerAdapter.getActiveAudioTrack();
  }

  setAudioTrack(trackId: string) {
    this.playerAdapter.setAudioTrack(trackId);
  }

  enableTextTrack(id: string) {
    if (this.textTrackMode === 'PlayerTextTracks') {
      this.playerAdapter.enableTextTrack(id);
    } else {
      this.vttTracksContainer?.enableTextTrack(id);
    }
  }

  disableTextTrack() {
    if (this.textTrackMode === 'PlayerTextTracks') {
      this.playerAdapter.disableTextTrack();
    } else {
      this.vttTracksContainer?.disableTextTrack();
    }
  }

  getTextTracks(): TextTrack[] {
    if (this.textTrackMode === 'PlayerTextTracks') {
      return this.playerAdapter.getTextTracks();
    } else {
      return this.vttTracksContainer?.getTextTracks() || ([] as TextTrack[]);
    }
  }

  getActiveTextTrack() {
    if (this.textTrackMode === 'PlayerTextTracks') {
      return this.playerAdapter.getActiveTextTrack();
    } else {
      return this.vttTracksContainer?.getActiveTextTrack();
    }
  }

  loadTextTracks(vttFiles: SourceTextTrack[]) {
    return this.getVttTracksContainer().loadTextTracks(vttFiles);
  }

  useVttTracks(): void {
    if (this.textTrackMode !== 'Vtt') {
      if (this.playerAdapter.getActiveTextTrack() !== undefined) {
        this.playerAdapter.disableTextTrack();
      }
      this.textTrackMode = 'Vtt';
    }
  }

  usePlayerTextTracks(): void {
    if (this.textTrackMode !== 'PlayerTextTracks') {
      if (this.getVttTracksContainer().getActiveTextTrack() !== undefined) {
        this.getVttTracksContainer().disableTextTrack();
      }
      this.textTrackMode = 'PlayerTextTracks';
    }
  }

  getTextTrackMode(): 'Vtt' | 'PlayerTextTracks' {
    return this.textTrackMode;
  }

  private getVttTracksContainer(): VttTracksContainer {
    if (this.vttTracksContainer) {
      return this.vttTracksContainer;
    }

    this.vttTracksContainer = new VttTracksContainer(this);
    return this.vttTracksContainer;
  }

  enableSubtitle(id: string) {
    this.enableTextTrack(id);
  }

  disableSubtitle() {
    this.disableTextTrack();
  }

  getSubtitles() {
    return this.getTextTracks();
  }

  getActiveSubtitles() {
    return this.getActiveTextTrack();
  }

  getPlayerAdapter() {
    return this.playerAdapter;
  }

  getVideoElement(): HTMLVideoElement | undefined {
    return this.playerAdapter.getVideoElement();
  }

  addEventListener<EventName extends string = FoundationEvent, EventType = FoundationEventBase>(
    type: EventName,
    callback: (event: EventType) => void,
  ): () => void {
    return this.playerContainer.addEventListener(type, callback);
  }

  removeEventListener<EventName extends string = FoundationEvent, EventType = FoundationEventBase>(
    type: EventName,
    callback: (event: EventType) => void,
  ): void {
    this.playerContainer.removeEventListener(type, callback);
  }

  listenOnce<EventName extends string = FoundationEvent, EventType = FoundationEventBase>(
    type: EventName,
    callback: (event: EventType) => void,
  ) {
    const listener = (e: EventType) => {
      callback(e);
      this.removeEventListener(type, listener);
    };
    this.addEventListener(type, listener);
  }

  dispatchEvent(event: Event): boolean {
    return this.playerContainer.dispatchEvent(event);
  }

  isDestroyable() {
    return this.destroyLocks.size === 0;
  }

  isDestroyed() {
    return this.destroyed;
  }

  addDestroyLock(lock: string) {
    this.destroyLocks.add(lock);
  }

  getDestroyLocks() {
    return Array.from(this.destroyLocks);
  }

  hasDestroyLock(lock: string) {
    return this.destroyLocks.has(lock);
  }

  removeDestroyLock(lock: string) {
    this.destroyLocks.delete(lock);
  }

  getPlayerElement() {
    return this.playerContainer.getPlayerElement();
  }

  getPlayerConfig() {
    return this.playerConfig;
  }

  getMediaConfig<PluginConfig = Record<string, unknown>>() {
    return this.playbackSession?.mediaConfig as
      | (Omit<MediaConfig, 'plugins'> & { plugins?: MediaConfig['plugins'] & PluginConfig })
      | undefined;
  }

  async destroy(cause: string | FoundationError | FoundationErrorEvent = 'client', force = false): Promise<boolean> {
    if (this.isDestroyed()) {
      return true;
    }

    if (!this.isDestroyable() && !force) {
      return false;
    }

    if (this.advertisingAdapter?.isAd()) {
      try {
        await this.advertisingAdapter.stopAdSlot('player was destroyed while ad was running');
      } catch (err) {
        this.log.error('stopAdSlot during destroy failed:', err);
      }
    }

    this.dispatchEvent(new FoundationDestroy(PlayerEvent.Destroy, { cause }));

    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = undefined;
    }

    this.advertisingAdapter?.destroy();
    try {
      await this.playerAdapter.destroy(cause);
    } catch (err) {
      this.log.error('destroying playerAdapter failed:', err);
    }
    this.playerContainer.destroy();
    this.destroyed = true;
    this.dispatchEvent(new FoundationDestroy(PlayerEvent.Destroyed, { cause }));
    return true;
  }

  private async stopAdSlotIfNecessary() {
    if (this.advertisingAdapter?.isAd()) {
      try {
        await this.advertisingAdapter.stopAdSlot('content was loaded while ad was running');
      } catch (err) {
        this.log.error('stopAdSlot during unloadPlayer failed:', err);
      }
    }
  }

  getId(): string {
    return this.playerId;
  }

  getBufferedSeconds() {
    return this.playerAdapter.getBufferedSeconds();
  }

  private getActiveAdSlot(ignoreNonLinear = false): AdSlot | undefined {
    if (!this.advertisingAdapter) {
      return;
    }

    if (!this.advertisingAdapter.isAd()) {
      return;
    }

    const currentAdSlot = this.advertisingAdapter.getCurrentAdSlot();
    if (!currentAdSlot) {
      return;
    }

    if (ignoreNonLinear && currentAdSlot.getType() === AdSlotType.NonLinear) {
      return;
    }

    return currentAdSlot.isActive() ? currentAdSlot : undefined;
  }

  /**
   * {@inheritDoc PlayerInterface.getLoaderConfig}
   */
  getLoaderConfig() {
    return this.userConfig;
  }

  /**
   * {@inheritDoc PlayerInterface.getRemoteConfig}
   */
  getRemoteConfig() {
    return this.remoteConfig;
  }

  /**
   * {@inheritDoc PlayerInterface.getClientSupport}
   */
  getClientSupport() {
    return this.clientSupport;
  }

  getStUSDebugInformation() {
    return this.stusResolver.getDebugInformation();
  }

  /**
   * {@inheritDoc PlayerInterface.getMedia}
   */
  getMedia() {
    return this.mediaManager.getMedia();
  }

  getPlaybackSession() {
    return this.playbackSession;
  }

  /**
   * {@inheritDoc PlayerInterface.getMedia}
   */
  setMetadata(metadata: UpdatableMediaMetadata) {
    this.dispatchEvent(new MetadataUpdatedEvent(metadata));
  }

  private hasPosterFrameBug(): boolean {
    if (!isAndroid() || !isChromiumEngine()) {
      return false;
    }

    const engineVersion = getEngineVersion();
    const majorVersion = Number(engineVersion.split('.')[0]);

    return majorVersion >= 122;
  }

  timeShift(offsetInSeconds: number): void {
    if (this.canTimeShift()) {
      this.playerAdapter.timeShift(offsetInSeconds);
    }
  }

  seekToLiveEdge(): void {
    if (this.canTimeShift()) {
      this.playerAdapter.seekToLiveEdge();
    }
  }

  getDVRWindowInSeconds(): number {
    if (this.canTimeShift()) {
      return this.playerAdapter.getDVRWindowInSeconds();
    }
    return 0;
  }

  distanceToLiveEdgeInSeconds(): number {
    if (this.canTimeShift()) {
      return this.playerAdapter.distanceToLiveEdgeInSeconds();
    }
    return 0;
  }

  private canTimeShift() {
    if (this.getActiveAdSlot(true)) {
      return false;
    }
    const mediaConfig = this.getMediaConfig();

    if (!mediaConfig) {
      return false;
    }

    if (!([SourceType.Live, SourceType.Event] as SourceType[]).includes(mediaConfig.type)) {
      throw new FoundationErrorEvent(PlayerEvent.Error, {
        message: 'The current playing content is not a live stream',
        code: PlayerError.OPERATION_NOT_SUPPORTED_35500.code,
      });
    }

    if (this.mediaManager.getContentState() !== ContentState.Started) {
      return false;
    }

    return true;
  }

  isYospaceStream(): boolean {
    return this.mediaManager.getMedia()?.source.isYospace || false;
  }
}
